"use client";

import React, { useEffect, useRef, useState } from "react";
import { Search, ShieldCheck, FileText, Stamp, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const roadmapSteps = [{
  id: "01",
  title: "Discovery & Curation",
  description: "We identify and curate high-potential, registered trademarks that align with your brand vision and expansion goals, saving you months of search time.",
  legalRef: "Initial Assessment",
  icon: Search
}, {
  id: "02",
  title: "Legal Vetting",
  description: "Comprehensive due diligence is conducted to ensure the trademark is free from encumbrances, disputes, and is legally sound for immediate transfer.",
  legalRef: "Verification Process",
  icon: ShieldCheck
}, {
  id: "03",
  title: "Agreement & Structuring",
  description: "Drafting and negotiating the precise terms of acquisition or licensing to protect both buyer and seller interests with absolute clarity.",
  legalRef: "Contractual Framework",
  icon: FileText
}, {
  id: "04",
  title: "Transfer & Execution",
  description: "End-to-end legal coordination for the official transfer of ownership or licensing rights, ensuring full compliance with regulatory standards.",
  legalRef: "Trade Marks Act, 1999",
  icon: Stamp
}];
function TimelineItem({
  step,
  index
}: {
  step: (typeof roadmapSteps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className={`relative flex items-center justify-between md:justify-normal w-full mb-12 md:mb-24 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ${isEven ? "md:flex-row-reverse" : ""}`}>
      {}
      <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-primary border-4 border-background z-10 shadow-[0_0_0_4px_var(--color-background)]" />
      
      {}
      <div className="md:hidden absolute left-[1.375rem] -translate-x-1/2 w-4 h-4 rounded-full bg-primary border-[3px] border-background z-10" />

      {}
      <div className="hidden md:block w-1/2" />

      {}
      <div className={`w-full pl-14 md:pl-0 md:w-1/2 ${isEven ? "md:pr-16 lg:pr-24" : "md:pl-16 lg:pl-24"}`}>
        <Card className="relative overflow-hidden border-border/50 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-card/50 backdrop-blur-sm">
          {}
          <div className="absolute -right-4 -top-6 text-[6rem] md:text-[8rem] font-black text-muted/30 select-none pointer-events-none leading-none tracking-tighter">
            {step.id}
          </div>
          
          <CardHeader className="relative z-10 pb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-primary/10 rounded-lg text-primary">
                <step.icon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold tracking-widest uppercase text-primary/80">
                {step.legalRef}
              </span>
            </div>
            <CardTitle className="text-2xl md:text-3xl font-bold text-foreground">
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-10">
            <p className="text-muted-foreground leading-relaxed">
              {step.description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function TheTransactionRoadmap() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="the-transaction-roadmap" className="bg-background py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10">
        <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[40%] h-[40%] rounded-full bg-secondary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 md:mb-32 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-sm font-bold tracking-widest uppercase text-primary mb-4">
            The Process
          </h2>
          <h3 className="text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">
            The Transaction Roadmap
          </h3>
          <p className="text-lg text-muted-foreground">
            A structured, legally compliant process designed to move you from discovery to ownership in days, not months.
          </p>
        </div>

        {}
        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="hidden md:block absolute left-1/2 top-4 bottom-4 w-px bg-gradient-to-b from-primary/0 via-primary/50 to-primary/0 -translate-x-1/2" />
          
          {}
          <div className="md:hidden absolute left-[1.375rem] top-4 bottom-4 w-px bg-gradient-to-b from-primary/0 via-primary/50 to-primary/0" />

          {}
          <div className="relative z-10">
            {roadmapSteps.map((step, i) => <TimelineItem key={step.id} step={step} index={i} />)}
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-24 text-center flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button size="lg" asChild className="rounded-full px-8">
            <Link to="/OurCataloguePage">
              Explore Available Brands
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild className="rounded-full px-8">
            <Link to="/HowItWorksPage">
              Learn More About The Process
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}